import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Search, Plus, Pencil, Trash2, Boxes, Loader2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';

const CATEGORIES = ['Material', 'Alat', 'Habis Pakai', 'Lainnya'];
const UNITS = ['Pcs','Batang','Meter','M2','M3','Kg','Sak','Liter','Roll','Lembar','Set','Buah','Hari','Kubik'];

export default function MaterialsPage() {
  const { can } = useAuth();
  const canWrite = can('business.write');
  const canDelete = can('business.delete');
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/materials', { params: { search: search || undefined, category } });
      setItems(r.data);
    } catch (e) { toast.error('Gagal memuat'); } finally { setLoading(false); }
  }, [search, category]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="animate-in-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2"><Boxes className="w-6 h-6 text-[var(--fpccs-primary)]" /> Master Material</h1>
          <p className="text-sm text-muted-fpccs mt-1">Daftar material untuk autocomplete transaksi. Harga tidak disimpan di sini, selalu diambil dari transaksi.</p>
        </div>
        {canWrite && <Button data-testid="btn-add-material" onClick={() => { setEditing(null); setOpen(true); }} className="bg-[var(--fpccs-primary)] hover:bg-[var(--fpccs-primary-2)] text-[var(--fpccs-bg)]">
          <Plus className="w-4 h-4 mr-1.5" /> Tambah Material
        </Button>}
      </div>

      <div className="card-fpccs p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fpccs-text-subtle)]" />
          <Input data-testid="material-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama material..." className="pl-9 bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="material-category-filter" className="w-full sm:w-[200px] bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]"><SelectValue placeholder="Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="card-fpccs overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[var(--fpccs-border)] hover:bg-transparent">
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Nama Material</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Kategori</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Satuan Default</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Status</TableHead>
              <TableHead className="w-24 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => <TableRow key={i}>{[...Array(5)].map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>)
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-fpccs py-10">Belum ada material. Tambahkan material atau input transaksi — sistem akan menawarkan untuk menambahkannya.</TableCell></TableRow>
            ) : items.map((m) => (
              <TableRow key={m.id} data-testid={`material-row-${m.id}`} className="border-b border-[var(--fpccs-border)] hover:bg-white/[0.03]">
                <TableCell className="font-medium">{m.name}</TableCell>
                <TableCell className="text-xs text-muted-fpccs">{m.category}</TableCell>
                <TableCell className="text-xs text-muted-fpccs">{m.default_unit}</TableCell>
                <TableCell><span className={`text-[11px] px-1.5 py-0.5 rounded border ${m.status === 'active' ? 'bg-[rgba(47,191,113,0.14)] text-[#2FBF71] border-[rgba(47,191,113,0.28)]' : 'bg-[rgba(224,90,79,0.14)] text-[#E05A4F] border-[rgba(224,90,79,0.28)]'}`}>{m.status || 'active'}</span></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {canWrite && <Button size="icon" variant="ghost" data-testid={`btn-edit-material-${m.id}`} onClick={() => { setEditing(m); setOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>}
                    {canDelete && <DeleteBtn testid={`btn-delete-material-${m.id}`} onConfirm={async () => { await api.delete(`/materials/${m.id}`); toast.success('Terhapus'); load(); }} />}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <MaterialFormDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </div>
  );
}

function MaterialFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState({ name: '', category: 'Material', default_unit: 'Pcs', status: 'active' });
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    if (open) setForm(initial ? { name: initial.name, category: initial.category || 'Material', default_unit: initial.default_unit || 'Pcs', status: initial.status || 'active' } : { name: '', category: 'Material', default_unit: 'Pcs', status: 'active' });
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nama wajib'); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/materials/${initial.id}`, form);
      else await api.post('/materials', form);
      toast.success(isEdit ? 'Diperbarui' : 'Ditambahkan');
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error('Gagal'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="material-form-dialog" className="max-w-md bg-[var(--fpccs-surface-1)]">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Material' : 'Tambah Material'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs text-muted-fpccs">Nama *</Label><Input data-testid="material-name-input" required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-muted-fpccs">Kategori</Label>
              <Input data-testid="material-category-input" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} placeholder="cth: Material, Alat, Habis Pakai" />
            </div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-fpccs">Satuan Default</Label>
              <Input data-testid="material-unit-input" value={form.default_unit} onChange={(e) => setForm(f => ({ ...f, default_unit: e.target.value }))} placeholder="cth: Pcs, Batang, M2, Sak" />
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-fpccs">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="material-status-input"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Non-aktif</SelectItem></SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
            <Button type="submit" disabled={saving} data-testid="btn-save-material" className="bg-[var(--fpccs-primary)] hover:bg-[var(--fpccs-primary-2)] text-[var(--fpccs-bg)]">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Simpan'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteBtn({ testid, onConfirm }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="icon" variant="ghost" data-testid={testid} className="text-[var(--fpccs-danger)]"><Trash2 className="w-3.5 h-3.5" /></Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Hapus?</AlertDialogTitle><AlertDialogDescription>Tindakan tidak dapat dibatalkan.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={onConfirm} className="bg-[var(--fpccs-danger)] text-[var(--fpccs-bg)]">Hapus</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
